import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { ApiFehler } from "../api/client";
import { useBetriebLoeschen, useBetriebLoeschvorschau, useKundeLoeschen, useKundeLoeschvorschau } from "../api/hooks";
import { useAuth } from "../kontext/AuthKontext";
import { useToast } from "../kontext/ToastKontext";
import { dateigroesse } from "../lib/format";
import { Hinweis, Knopf, Modal, Spinner } from "./Basis";

/**
 * Endgültiges Löschen (nur Admin). Schutz gegen Versehen: eigener Dialog, Übersicht was wegfällt,
 * und der Knopf wird erst aktiv, wenn der Name exakt eingetippt ist. Der Server prüft dasselbe.
 */
function LoeschDialog({
  titel,
  name,
  laedtVorschau,
  gesperrt,
  uebersicht,
  loescht,
  fehler,
  beiLoeschen,
  schliessen,
}: {
  titel: string;
  name: string;
  laedtVorschau: boolean;
  gesperrt: string | null;
  uebersicht: ReactNode;
  loescht: boolean;
  fehler: string | null;
  beiLoeschen: () => void;
  schliessen: () => void;
}) {
  const [eingabe, setEingabe] = useState("");
  const passt = eingabe.trim() === name.trim();
  const moeglich = !laedtVorschau && !gesperrt;
  // Das Feld erscheint erst nach der Vorschau – dann fokussieren (Modal fokussiert nur beim Öffnen).
  const feld = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (moeglich) feld.current?.focus();
  }, [moeglich]);

  return (
    <Modal
      offen
      titel={titel}
      beiSchliessen={schliessen}
      fuss={
        <>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf variante="gefahr" disabled={!moeglich || !passt} laedt={loescht} onClick={beiLoeschen}>
            Endgültig löschen
          </Knopf>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {laedtVorschau ? (
          <Spinner label="Prüfe, was gelöscht würde…" />
        ) : gesperrt ? (
          <Hinweis art="warnung">{gesperrt}</Hinweis>
        ) : (
          <>
            <Hinweis art="fehler">Das lässt sich nicht rückgängig machen.</Hinweis>
            {uebersicht}
            <div>
              <label htmlFor="loesch-name" className="mb-1 block text-xs font-semibold">
                Zur Bestätigung den Namen eintippen: <span className="font-mono">{name}</span>
              </label>
              <input
                id="loesch-name"
                ref={feld}
                autoComplete="off"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2"
                value={eingabe}
                onChange={(e) => setEingabe(e.target.value)}
                onPaste={(e) => e.preventDefault()}
              />
            </div>
          </>
        )}
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </div>
    </Modal>
  );
}

function useIstAdmin(): boolean {
  const { benutzer } = useAuth();
  return benutzer?.rolle === "admin";
}

export function KundeLoeschenKnopf({ kunde }: { kunde: { id: number; name: string } }) {
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const vorschau = useKundeLoeschvorschau(kunde.id, offen);
  const loeschen = useKundeLoeschen(kunde.id);
  const toast = useToast();
  const navigate = useNavigate();
  if (!useIstAdmin()) return null;

  function schliessen() {
    setOffen(false);
    setFehler(null);
  }

  function beiLoeschen() {
    setFehler(null);
    loeschen.mutate(kunde.name, {
      onSuccess: () => {
        toast.erfolg("Kunde gelöscht", kunde.name);
        navigate("/anlagen", { replace: true });
      },
      onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Löschen fehlgeschlagen"),
    });
  }

  const v = vorschau.data;
  return (
    <>
      <Knopf variante="geist" className="text-xs text-ampel-rot" onClick={() => setOffen(true)}>
        Kunden löschen…
      </Knopf>
      {offen && (
        <LoeschDialog
          titel="Kunden endgültig löschen"
          name={kunde.name}
          laedtVorschau={vorschau.isLoading}
          gesperrt={vorschau.error ? "Die Vorschau konnte nicht geladen werden." : (v?.gesperrt_grund ?? null)}
          uebersicht={
            v && (
              <ul className="list-disc space-y-1 pl-5">
                <li>Kunde <span className="font-semibold">{kunde.name}</span> mit allen Angaben</li>
                <li>{v.anlagen} Anlage(n) mit Status-Links</li>
                <li>{v.dokumente} Dokument(e) mit Dateien ({dateigroesse(v.bytes)} Speicher)</li>
              </ul>
            )
          }
          loescht={loeschen.isPending}
          fehler={fehler}
          beiLoeschen={beiLoeschen}
          schliessen={schliessen}
        />
      )}
    </>
  );
}

export function BetriebLoeschenKnopf({ betrieb }: { betrieb: { id: number; name: string } }) {
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const vorschau = useBetriebLoeschvorschau(betrieb.id, offen);
  const loeschen = useBetriebLoeschen(betrieb.id);
  const toast = useToast();
  const navigate = useNavigate();
  if (!useIstAdmin()) return null;

  function schliessen() {
    setOffen(false);
    setFehler(null);
  }

  function beiLoeschen() {
    setFehler(null);
    loeschen.mutate(betrieb.name, {
      onSuccess: () => {
        toast.erfolg("Betrieb gelöscht", betrieb.name);
        navigate("/betriebe", { replace: true });
      },
      onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Löschen fehlgeschlagen"),
    });
  }

  const v = vorschau.data;
  return (
    <>
      <Knopf variante="geist" className="text-xs text-ampel-rot" onClick={() => setOffen(true)}>
        Betrieb löschen…
      </Knopf>
      {offen && (
        <LoeschDialog
          titel="Betrieb endgültig löschen"
          name={betrieb.name}
          laedtVorschau={vorschau.isLoading}
          gesperrt={vorschau.error ? "Die Vorschau konnte nicht geladen werden." : (v?.gesperrt_grund ?? null)}
          uebersicht={
            v && (
              <ul className="list-disc space-y-1 pl-5">
                <li>Betrieb <span className="font-semibold">{betrieb.name}</span> mit Stammdaten und Netzbetreiber-Zugängen</li>
                <li>{v.zugaenge} Mitarbeiter-Zugang/-Zugänge (können sich danach nicht mehr anmelden)</li>
              </ul>
            )
          }
          loescht={loeschen.isPending}
          fehler={fehler}
          beiLoeschen={beiLoeschen}
          schliessen={schliessen}
        />
      )}
    </>
  );
}
